// Failure Pattern Recognition Engine
// Matches live sensor behaviour against known failure signatures

export interface FailurePattern {
  id:          string;
  name:        string;
  icon:        string;
  description: string;
  rootCause:   string;
  fix:         string;
  matchScore:  number;  // 0–100 how well current data matches this pattern
}

// Each rule describes what sensors should be doing for a known failure mode
// Keys are sensor type keywords (matched against sensor name, case-insensitive)
interface PatternRule {
  id:          string;
  name:        string;
  icon:        string;
  description: string;
  rootCause:   string;
  fix:         string;
  // conditions: each entry is [sensorKeyword, required behaviour]
  conditions: {
    keyword:   string;
    slopeMin:  number;   // minimum slope to match
    stageMin:  'EARLY' | 'MID' | 'LATE' | 'NORMAL';
    required:  boolean;  // if false, matching boosts score but isn't required
  }[];
}

const STAGE_RANK: Record<string, number> = {
  NORMAL: 0, CALIBRATING: 0, EARLY: 1, MID: 2, LATE: 3,
};

const PATTERNS: PatternRule[] = [
  {
    id:          'bearing_failure',
    name:        'Bearing Failure',
    icon:        '⚙️',
    description: 'Vibration rises first, temperature follows 5–10 cycles later, speed unaffected',
    rootCause:   'Bearing wear — metal-on-metal friction increasing',
    fix:         'Replace bearings. Lubricate immediately to slow progression.',
    conditions: [
      { keyword: 'vibr',  slopeMin: 0.003, stageMin: 'EARLY', required: true  },
      { keyword: 'temp',  slopeMin: 0.002, stageMin: 'EARLY', required: false },
      { keyword: 'speed', slopeMin: -0.001, stageMin: 'NORMAL', required: false },
    ],
  },
  {
    id:          'overheating',
    name:        'Overheating',
    icon:        '🌡️',
    description: 'Temperature spikes fast, vibration stays normal, speed unaffected',
    rootCause:   'Cooling failure or blocked ventilation',
    fix:         'Check cooling system, clean vents, reduce load immediately.',
    conditions: [
      { keyword: 'temp',  slopeMin: 0.008, stageMin: 'MID',   required: true  },
      { keyword: 'vibr',  slopeMin: -0.001, stageMin: 'NORMAL', required: false },
    ],
  },
  {
    id:          'mechanical_wear',
    name:        'Mechanical Wear',
    icon:        '🔩',
    description: 'All sensors rising slowly and consistently — gradual degradation',
    rootCause:   'General mechanical wear across multiple components',
    fix:         'Schedule full maintenance inspection. Check all moving parts.',
    conditions: [
      { keyword: 'temp',  slopeMin: 0.002, stageMin: 'EARLY', required: true  },
      { keyword: 'vibr',  slopeMin: 0.002, stageMin: 'EARLY', required: true  },
      { keyword: 'speed', slopeMin: 0.001, stageMin: 'EARLY', required: false },
    ],
  },
  {
    id:          'speed_degradation',
    name:        'Speed / Load Degradation',
    icon:        '📉',
    description: 'Speed dropping while temperature and vibration rise — motor struggling under load',
    rootCause:   'Motor overload or drive belt/coupling failure',
    fix:         'Reduce load. Inspect drive coupling and motor windings.',
    conditions: [
      { keyword: 'speed', slopeMin: 0.002, stageMin: 'EARLY', required: true  },
      { keyword: 'temp',  slopeMin: 0.002, stageMin: 'EARLY', required: false },
    ],
  },
  {
    id:          'electrical_fault',
    name:        'Electrical / Power Fault',
    icon:        '⚡',
    description: 'Current/power rising sharply with speed dropping — electrical resistance increasing',
    rootCause:   'Winding insulation breakdown or power supply issue',
    fix:         'Measure winding resistance. Check power supply voltage and current draw.',
    conditions: [
      { keyword: 'power',    slopeMin: 0.005, stageMin: 'MID', required: true  },
      { keyword: 'energy',   slopeMin: 0.005, stageMin: 'MID', required: true  },
      { keyword: 'current',  slopeMin: 0.005, stageMin: 'MID', required: true  },
      { keyword: 'speed',    slopeMin: 0.001, stageMin: 'EARLY', required: false },
    ],
  },
  {
    id:          'pressure_leak',
    name:        'Pressure / Flow Leak',
    icon:        '💧',
    description: 'Pressure dropping while flow rate drops — system leak or valve failure',
    rootCause:   'Seal failure, pipe leak, or valve malfunction',
    fix:         'Inspect seals, pipes, and valves. Pressure test the system.',
    conditions: [
      { keyword: 'pressure', slopeMin: 0.002, stageMin: 'EARLY', required: true  },
      { keyword: 'flow',     slopeMin: 0.002, stageMin: 'EARLY', required: false },
    ],
  },
];

export function detectPatterns(
  sensorResults: Record<string, { stage: string; slope: number; calibrating: boolean }>
): FailurePattern[] {
  const active = Object.entries(sensorResults).filter(([, r]) => !r.calibrating);
  if (active.length === 0) return [];

  const matched: FailurePattern[] = [];

  for (const pattern of PATTERNS) {
    let score        = 0;
    let totalWeight  = 0;
    let requiredMet  = true;

    for (const cond of pattern.conditions) {
      const weight = cond.required ? 2 : 1;
      totalWeight += weight;

      // Find a sensor whose name contains the keyword
      const match = active.find(([name]) =>
        name.toLowerCase().includes(cond.keyword)
      );

      if (!match) {
        if (cond.required) { requiredMet = false; }
        continue;
      }

      const [, result] = match;
      const stageOk  = STAGE_RANK[result.stage] >= STAGE_RANK[cond.stageMin];
      const slopeOk  = result.slope >= cond.slopeMin;

      if (stageOk && slopeOk) {
        score += weight;
      } else if (cond.required) {
        requiredMet = false;
      }
    }

    if (!requiredMet) continue;
    if (totalWeight === 0) continue;

    const matchScore = Math.round((score / totalWeight) * 100);
    if (matchScore >= 40) {
      matched.push({
        id:          pattern.id,
        name:        pattern.name,
        icon:        pattern.icon,
        description: pattern.description,
        rootCause:   pattern.rootCause,
        fix:         pattern.fix,
        matchScore,
      });
    }
  }

  // Sort by match score descending
  return matched.sort((a, b) => b.matchScore - a.matchScore);
}
